import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/store/useStore';
import { apiFetch, referralApi, cardApi, skinApi, userApi, homeApi } from '@/utils/api';
import { v2Api } from '@/utils/v2Api';
import { getCached, setCached, TTL } from '@/utils/cache';
import { ProfileTiles } from './Bento/ProfileTiles';
import { MyListingsTab } from './Tabs/MyListingsTab';
import { MyBidsTab } from './Tabs/MyBidsTab';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import {
    ChevronLeft,
    Crown,
    Shield,
    LayoutTemplate,
    BadgeCheck,
    Wallet,
    TrendingUp,
    ShoppingBag,
    Handshake,
    Tag,
    Gavel,
    Layers,
    AtSign,
    Sparkles,
    Smile,
} from 'lucide-react';

type ProfileTab = 'overview' | 'listings' | 'bids';

/* ─── Animated count-up ─── */
const useCountUp = (target: number, enabled: boolean, delay = 0) => {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!enabled) return;
        let raf = 0;
        const start = performance.now() + delay * 1000;
        const duration = 800;
        const tick = (now: number) => {
            if (now < start) { raf = requestAnimationFrame(tick); return; }
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(target * eased);
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, enabled, delay]);
    return val;
};

const MoneyCell = ({ value, decimals = 1, enabled, delay = 0 }: any) => {
    const v = useCountUp(Number(value || 0), enabled, delay);
    return <>{v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</>;
};

const IntCell = ({ value, enabled, delay = 0 }: any) => {
    const v = useCountUp(Number(value || 0), enabled, delay);
    return <>{Math.round(v).toLocaleString()}</>;
};

const CustomEmoji104Player: React.FC = () => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const animInstance = React.useRef<any>(null);

    React.useEffect(() => {
        let isMounted = true;

        async function loadLottie(): Promise<any> {
            if ((window as any).lottie) return (window as any).lottie;
            return new Promise((resolve, reject) => {
                const existing = document.getElementById('lottie-script');
                if (existing) {
                    existing.addEventListener('load', () => resolve((window as any).lottie));
                    return;
                }
                const script = document.createElement('script');
                script.id = 'lottie-script';
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js';
                script.onload = () => resolve((window as any).lottie);
                script.onerror = reject;
                document.head.appendChild(script);
            });
        }

        async function init() {
            const urls = [
                '/emojis/5967522716062847679.json',
                '/emojis/5987802868734760945.json'
            ];
            let animationData: any = null;

            for (const u of urls) {
                try {
                    const res = await fetch(u);
                    if (res.ok) {
                        const text = await res.text();
                        if (text.startsWith('{')) {
                            animationData = JSON.parse(text);
                            break;
                        }
                    }
                } catch (_) {}
            }

            if (!animationData) return;

            try {
                const lottieObj = await loadLottie();
                if (!isMounted || !containerRef.current || !lottieObj) return;
                if (animInstance.current) animInstance.current.destroy();
                animInstance.current = lottieObj.loadAnimation({
                    container: containerRef.current,
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    animationData
                });
            } catch (err) {
                console.warn('[Emoji104] Error initializing lottie:', err);
            }
        }

        init();

        return () => {
            isMounted = false;
            if (animInstance.current) animInstance.current.destroy();
        };
    }, []);

    return (
        <div className="absolute -bottom-2 -right-2 z-20 w-8 h-8 flex items-center justify-center drop-shadow-md pointer-events-none">
            <div ref={containerRef} className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:block" />
        </div>
    );
};

const Profile = ({ onBack, onNavigate, isActive }: any) => {
    const { t } = useTranslation('profile');
    const { t: tc } = useTranslation('common');
    const { user, wallet } = useStore();
    const { impactOccurred } = useHapticFeedback();
    const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
    const [statsRendered, setStatsRendered] = useState(false);

    // V2 stats / rating / summary
    const [v2Stats, setV2Stats] = useState<any>(null);
    const [v2Rating, setV2Rating] = useState<any>(null);
    const [v2UserSummary, setV2UserSummary] = useState<any>(null);

    useEffect(() => {
        if (!user?.telegram_id) return;
        const uid = user.telegram_id as any;
        v2Api.stats(uid).then(d => { if (d.success) setV2Stats(d.stats); }).catch(console.error);
        v2Api.rating(uid).then(d => { if (d.success) setV2Rating(d.rating); }).catch(console.error);
        v2Api.summary(uid).then(d => { if (d.success) setV2UserSummary(d.summary || d.user); }).catch(console.error);
    }, [user?.telegram_id]);

    useEffect(() => {
        const t = setTimeout(() => setStatsRendered(true), 120);
        return () => clearTimeout(t);
    }, []);

    // Data States
    const [myCards, setMyCards] = useState<any[]>([]);
    const [likedCards, setLikedCards] = useState<any[]>([]);
    const [myBrands, setMyBrands] = useState<any[]>([]);
    const [equippedSkin, setEquippedSkin] = useState<any>(null);
    const [mySkins, setMySkins] = useState<any[]>([]);
    const [tileBgs, setTileBgs] = useState<any>({});

    // Stale-While-Revalidate
    const loadProfileData = useCallback(async (userId: string, force = false) => {
        const cacheKey = `profile_${userId}`;
        if (!force) {
            const cached = getCached<{ cards: any[]; skins: any[]; brands: any[]; skin: any; bgs: any }>(cacheKey, TTL.CARDS);
            if (cached) {
                setMyCards(cached.cards);
                setMySkins(cached.skins);
                setMyBrands(cached.brands);
                setEquippedSkin(cached.skin);
                setTileBgs(cached.bgs || {});
            }
        }

        try {
            const [skinEquippedRes, skinsRes, cardsRes, brandsRes, likesRes, uiRes] = await Promise.allSettled([
                skinApi.getEquipped().then(r => r.json()),
                skinApi.getUserSkins(userId).then(r => r.json()),
                cardApi.getUserCards(userId).then(r => r.json()),
                apiFetch(`/api/users/${userId}/brands`).then(r => r.json()),
                userApi.getLikes(userId).then((r: any) => r.json()),
                homeApi.getProfileTileBgs().then((r: any) => r.json()),
            ]);

            const newSkin = skinEquippedRes.status === 'fulfilled' && skinEquippedRes.value.success
                ? skinEquippedRes.value.skin : equippedSkin;
            const newSkins = skinsRes.status === 'fulfilled' && skinsRes.value.success
                ? skinsRes.value.skins : mySkins;
            const rawCards = cardsRes.status === 'fulfilled'
                ? (Array.isArray(cardsRes.value.cards) ? cardsRes.value.cards : (Array.isArray(cardsRes.value) ? cardsRes.value : []))
                : myCards;
            const newBrands = brandsRes.status === 'fulfilled' && brandsRes.value.success
                ? brandsRes.value.brands : myBrands;
            const rawLikes = likesRes.status === 'fulfilled' && likesRes.value.success
                ? (likesRes.value.likes || []) : [];
            const newBgs = uiRes.status === 'fulfilled' && uiRes.value.success
                ? (uiRes.value.backgrounds || {}) : tileBgs;

            setEquippedSkin(newSkin);
            setMySkins(newSkins);
            setMyCards(rawCards);
            setLikedCards(rawLikes);
            setMyBrands(newBrands);
            setTileBgs(newBgs);

            setCached(cacheKey, { cards: rawCards, skins: newSkins, brands: newBrands, skin: newSkin, bgs: newBgs });
            setCached(`${cacheKey}_ts`, Date.now());
        } catch (e) {
            console.error('[Profile] Data fetch error:', e);
        }
    }, []);

    useEffect(() => {
        if (!user?.telegram_id) return;
        loadProfileData(user.telegram_id);
        referralApi.getStats(user.telegram_id).catch(console.error);
    }, [user?.telegram_id]);

    const level = Number(v2UserSummary?.level ?? user?.level ?? 1);
    const xpProgress = Math.min(100, Math.max(0, Number(user?.xp_progress) || 0));
    const currentXp = Math.floor(Number(user?.current_xp) || 0);
    const isDev = String(user?.telegram_id) === '601287447';
    const verification = String(v2UserSummary?.verification_status || (user as any)?.verification_status || 'unverified').replace('_', ' ');
    const rating = v2UserSummary?.rating != null
        ? Number(v2UserSummary.rating).toFixed(1)
        : (v2Rating?.average_rating != null ? Number(v2Rating.average_rating).toFixed(1) : '—');
    const role = String(v2UserSummary?.role || user?.role || 'user').toUpperCase();

    const listingsCount = v2Stats ? (v2Stats.active_listings || 0) + (v2Stats.sold_listings || 0) : null;
    const winsCount = v2Stats?.auctions_won ?? null;

    const handleNavigate = (route?: string, data?: any) => {
        impactOccurred('light');
        if (route === 'my_listings') setActiveTab('listings');
        else if (route === 'my_bids') setActiveTab('bids');
        else if (route) onNavigate?.(route, data);
    };

    const navBtn = 'w-10 h-10 rounded-[14px] glass-chip flex items-center justify-center text-white active:scale-95 transition-transform';

    return (
        <div className="min-h-screen bg-tg-bg text-tg-text font-sans selection:bg-tg-button/20 pb-safe relative overflow-hidden">
            {/* ══════════ GLASS NAV BAR ══════════ */}
            <div className="relative z-10 px-4 pt-[calc(var(--tg-safe-area-inset-top,0px)+10px)]">
                <div className="glass rounded-[22px] px-3 py-2.5 flex items-center justify-between">
                    <button
                        onClick={() => { impactOccurred('light'); onBack?.(); }}
                        className={navBtn}
                        title="Back"
                    >
                        <ChevronLeft size={18} />
                    </button>

                    <div className="flex items-center gap-1.5">
                        {['admin_card', 'admin', 'admin_emojis', 'template_preview'].map((route, idx) => {
                            const icon = idx === 0 ? <Crown size={15} /> : idx === 1 ? <Shield size={15} /> : idx === 2 ? <Smile size={15} /> : <LayoutTemplate size={15} />;
                            const color = idx === 0 ? 'text-purple-300 border-purple-500/25' : idx === 1 ? 'text-red-300 border-red-500/25' : idx === 2 ? 'text-amber-300 border-amber-500/25' : 'text-cyan-300 border-cyan-500/25';
                            const isAdminUser = (user as any)?.role === 'admin' || String(user?.telegram_id) === '601287447';
                            return isAdminUser ? (
                                <button
                                    key={route}
                                    onClick={() => { impactOccurred('medium'); onNavigate?.(route); }}
                                    className={cn(navBtn, 'border', color)}
                                    title={route}
                                >
                                    {icon}
                                </button>
                            ) : null;
                        })}
                    </div>
                </div>
            </div>

            {/* ══════════ PROFILE HERO — pure glass, NO cover image ══════════ */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03, type: 'spring', stiffness: 240, damping: 26 }}
                className="relative z-10 px-4 mt-3"
            >
                <div className="glass rounded-[30px] p-5 relative overflow-hidden">
                    <div className="glass-sweep rounded-[30px]" />

                    <div className="relative flex items-center gap-4">
                        {/* Avatar + Level pill */}
                        <div className="relative shrink-0">
                            <div className="glass-inset w-20 h-20 rounded-[24px] p-[3px] relative z-10">
                                <div className="w-full h-full rounded-[20px] overflow-hidden">
                                    <img
                                        src={user?.photo_url || '/icons/default-avatar.svg'}
                                        alt="avatar"
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.currentTarget.src = '/icons/default-avatar.svg'; }}
                                    />
                                </div>
                            </div>
                            <CustomEmoji104Player />
                        </div>

                        {/* Identity */}
                        <div className="flex-1 min-w-0">
                            <h1 className="text-[20px] font-black text-white tracking-tighter truncate flex items-center gap-2">
                                {user?.first_name} {user?.last_name}
                                {isDev && (
                                    <span className="bg-gradient-to-r from-[#B4F73D]/20 to-teal-400/20 text-[#B4F73D] border border-[#B4F73D]/30 px-1.5 py-0.5 rounded-md text-[9px] uppercase font-black tracking-widest shrink-0">
                                        Developer
                                    </span>
                                )}
                            </h1>
                            <div className="flex items-center flex-wrap gap-1.5 mt-2">
                                <span className="inline-flex items-center gap-1 text-[10px] font-black text-white/70 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-lg">
                                    <AtSign size={10} className="text-cyan-300" /> {user?.username || 'user'}
                                </span>
                                <span className="text-[9px] px-2 py-1 rounded-lg bg-white/[0.06] border border-white/10 text-white/40 font-mono font-bold">
                                    ID {user?.telegram_id}
                                </span>
                                {String(verification).toLowerCase() === 'verified' && (
                                    <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/25 px-2 py-1 rounded-lg">
                                        <BadgeCheck size={10} /> Verified
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Quick facts row */}
                    <div className="relative grid grid-cols-3 gap-2 mt-5">
                        {([
                            { label: 'Role', value: role },
                            { label: 'Status', value: String(v2UserSummary?.status || (user as any)?.status || 'active').toUpperCase() },
                            { label: 'Rating', value: rating },
                        ]).map((f, i) => (
                            <div key={i} className="glass-inset rounded-2xl px-2 py-2.5 text-center">
                                <p className="text-[13px] font-black text-white tracking-tight truncate">{f.value}</p>
                                <p className="text-[8px] font-bold text-white/40 uppercase tracking-widest mt-0.5">{f.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* XP progress */}
                    <div className="relative mt-4">
                        <div className="flex justify-between text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">
                            <span className="flex items-center gap-1">
                                <Sparkles size={11} className="text-amber-300" /> Level {level} · {currentXp.toLocaleString()} XP
                            </span>
                            <span className="text-white/70">{Math.round(xpProgress)}%</span>
                        </div>
                        <div className="h-2.5 bg-black/40 rounded-full overflow-hidden border border-white/10">
                            <div
                                className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.7)]"
                                style={{ width: `${xpProgress}%` }}
                            />
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ══════════ WALLET / HOLDINGS ══════════ */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.07, type: 'spring', stiffness: 240, damping: 26 }}
                className="relative z-10 px-4 mt-3"
            >
                <div className="glass rounded-[28px] p-4 relative overflow-hidden">
                    <div className="pointer-events-none absolute bottom-[-50%] left-[-15%] w-[60%] h-[70%] bg-cyan-500/10 blur-[60px] rounded-full" />
                    <div className="relative flex items-center justify-between mb-3">
                        <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/50">
                            <Wallet size={12} className="text-cyan-300" /> Wallet & Holdings
                        </p>
                        <span className="text-[9px] px-2 py-1 rounded-lg bg-cyan-500/10 border border-cyan-400/20 text-cyan-300 font-black uppercase tracking-widest">
                            Live
                        </span>
                    </div>
                    <div className="relative grid grid-cols-3 gap-2">
                        {([
                            { label: 'Balance', val: <><MoneyCell value={wallet?.available_balance ?? 0} enabled={statsRendered} /><span className="text-[8px] text-cyan-300/70 font-black"> GRAM</span></> },
                            { label: 'Total Earned', val: <><MoneyCell value={wallet?.total_earned ?? 0} enabled={statsRendered} /><span className="text-[8px] text-white/40 font-black"> GRAM</span></> },
                            { label: 'My Cards', val: <IntCell value={myCards.length} enabled={statsRendered} /> },
                        ]).map((cell, i) => (
                            <div key={i} className="glass-inset rounded-2xl px-2 py-3 text-center">
                                <p className="text-[14px] font-black text-white tracking-tight leading-5">{cell.val}</p>
                                <p className="text-[8px] font-bold text-white/40 uppercase tracking-widest mt-1">{cell.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* ══════════ MARKET ACTIVITY — always visible, no hiding ══════════ */}
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.11, type: 'spring', stiffness: 240, damping: 26 }}
                className="relative z-10 px-4 mt-3"
            >
                <div className="glass rounded-[28px] p-4 relative overflow-hidden">
                    <div className="pointer-events-none absolute top-[-50%] right-[-15%] w-[60%] h-[70%] bg-fuchsia-500/10 blur-[60px] rounded-full" />
                    <div className="relative flex items-center justify-between mb-3">
                        <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-white/50">
                            <Layers size={12} className="text-purple-300" /> Market Activity
                        </p>
                        <span className="text-[9px] px-2 py-1 rounded-lg bg-purple-500/10 border border-purple-400/20 text-purple-300 font-black uppercase tracking-widest">
                            Stats
                        </span>
                    </div>

                    {/* Money row */}
                    <div className="relative grid grid-cols-2 gap-2 mb-2">
                        {([
                            { label: 'Received', val: <><MoneyCell value={v2Stats?.total_received ?? 0} enabled={statsRendered} /><span className="text-[8px] text-emerald-300/80 font-black"> GRAM</span></>, icon: <TrendingUp size={12} className="text-emerald-300" /> },
                            { label: 'Spent', val: <><MoneyCell value={v2Stats?.total_spent ?? 0} enabled={statsRendered} /><span className="text-[8px] text-rose-300/80 font-black"> GRAM</span></>, icon: <TrendingUp size={12} className="text-rose-300 rotate-180" /> },
                        ]).map((cell, i) => (
                            <div key={i} className="rounded-2xl px-3 py-3 bg-white/[0.04] border border-white/10">
                                <p className="flex items-center gap-1.5 text-[9px] font-bold text-white/50 uppercase tracking-widest mb-1">
                                    {cell.icon} {cell.label}
                                </p>
                                <p className="text-[15px] font-black text-white font-mono tracking-tight">{cell.val}</p>
                            </div>
                        ))}
                    </div>

                    {/* 4 counters */}
                    <div className="relative grid grid-cols-4 gap-2">
                        {([
                            { icon: <ShoppingBag size={13} className="text-blue-300" />, label: 'Sales', val: v2Stats?.total_sales, count: true },
                            { icon: <Handshake size={13} className="text-purple-300" />, label: 'Purchases', val: v2Stats?.total_purchases, count: true },
                            { icon: <Tag size={13} className="text-cyan-300" />, label: 'Listings', val: v2Stats ? `${(v2Stats.active_listings || 0)}/${(v2Stats.sold_listings || 0)}` : null, count: false },
                            { icon: <Gavel size={13} className="text-orange-300" />, label: 'Won', val: v2Stats?.auctions_won, count: true },
                        ]).map((t, i) => (
                            <div key={i} className="glass-inset rounded-2xl px-1 py-3 flex flex-col items-center gap-1">
                                <span className="text-white/60">{t.icon}</span>
                                <span className="text-[13px] font-black text-white tracking-tight">
                                    {t.count && t.val != null ? <IntCell value={t.val} enabled={statsRendered} delay={0.15 + i * 0.05} /> : (t.val ?? '—')}
                                </span>
                                <span className="text-[7px] font-bold text-white/35 uppercase tracking-widest">{t.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </motion.div>

            {/* ══════════ FILTER BAR (glass chips) ══════════ */}
            <div className="relative z-30 sticky top-0 px-4 pt-3 pb-3 bg-gradient-to-b from-tg-bg via-tg-bg/90 to-transparent">
                <div className="glass-strong rounded-[22px] p-1.5 grid grid-cols-3 gap-1.5">
                    {([
                        { id: 'overview', label: 'Showroom', icon: <Layers size={14} /> },
                        { id: 'listings', label: 'Listings', icon: <Tag size={14} />, badge: listingsCount },
                        { id: 'bids', label: 'Bids', icon: <Gavel size={14} />, badge: winsCount },
                    ]).map(tab => {
                        const isActiveTab = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => { impactOccurred('light'); setActiveTab(tab.id as ProfileTab); }}
                                className={cn(
                                    "relative flex flex-col items-center gap-0.5 py-2 rounded-[16px] font-black text-[10px] uppercase tracking-widest transition-all duration-300",
                                    isActiveTab ? "text-white" : "text-white/40 hover:bg-white/5"
                                )}
                            >
                                {isActiveTab && (
                                    <motion.div
                                        layoutId="profileGlassChip"
                                        className="absolute inset-0 rounded-[16px] bg-white/[0.08] border border-white/15"
                                        transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10 flex items-center gap-1">
                                    {tab.icon}
                                    {tab.badge != null && (
                                        <span className="text-[8px] px-1 rounded-md bg-white/10 text-white/70 font-black">{tab.badge}</span>
                                    )}
                                </span>
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ══════════ CONTENT ══════════ */}
            <div className="relative z-10">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        {activeTab === 'overview' && (
                            <ProfileTiles
                                user={user}
                                myCards={myCards}
                                mySkins={mySkins}
                                myBrands={myBrands}
                                likedCards={likedCards}
                                tileBgs={tileBgs}
                                isActive={isActive}
                                onNavigate={handleNavigate}
                            />
                        )}

                        {activeTab === 'listings' && (
                            <MyListingsTab onNavigate={handleNavigate} />
                        )}

                        {activeTab === 'bids' && (
                            <MyBidsTab onNavigate={handleNavigate} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="h-6" />
        </div>
    );
};

export default Profile;